import type { ChatUiRentalPriceChange } from '@voll-intelligence/genui-contract';
import { fmtMoney } from '../../../shared/ui/flight_format.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../shared/ui/flight_ui.css';
import '../../shared/rental_ui.css';

/**
 * Mudança de preço: flat (sem card), alinhado ao texto da LLM. A prosa do agente
 * (`note`) carrega o contexto ("o preço mudou…"); aqui mostramos só a transição
 * total antigo → novo + os dois CTAs renderizados pelo agente. "Continuar mesmo
 * assim" segue com o novo preço (via `interaction` quando há `challengeId`, senão
 * `reply`); "Cancelar" volta para a busca. Em `readOnly` congela sem ações.
 */
export function RentalPriceChangeCard({
  priceChange,
  note,
  readOnly = false,
  onContinue,
  onCancel,
}: {
  readonly priceChange: ChatUiRentalPriceChange;
  readonly note?: string;
  readonly readOnly?: boolean;
  readonly onContinue?: () => void;
  readonly onCancel?: () => void;
}) {
  const { t } = useGenUiI18n();
  const { renderMarkdown } = useGenUiConfig();
  const { previousPrice, newPrice } = priceChange;
  const increased = newPrice.amount > previousPrice.amount;
  return (
    <div className="itin rui-price-change">
      {note && <div className="itin-intro">{renderMarkdown ? renderMarkdown(note) : note}</div>}
      <div className="rpc-prices">
        <span className="rpc-old">{fmtMoney(previousPrice)}</span>
        <span className="rpc-arrow" aria-hidden="true">
          →
        </span>
        <span className={`rpc-new${increased ? ' is-up' : ' is-down'}`}>{fmtMoney(newPrice)}</span>
      </div>
      {!readOnly && (
        <div className="rpc-actions">
          <button type="button" className="fui-btn fui-btn-primary fui-btn-sm" onClick={onContinue}>
            {t('rental.priceChange.continue')}
          </button>
          <button type="button" className="fui-btn fui-btn-sm" onClick={onCancel}>
            {t('rental.priceChange.cancel')}
          </button>
        </div>
      )}
    </div>
  );
}
